import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  getCustomPageRoute,
  getCustomPageSidebarWarnings,
  getSidebarItemIconClass,
  shouldShowLabCardImagePreview,
  ALL_SIDEBAR_ICON_PRESET_OPTIONS,
  SIDEBAR_ICON_PRESET_GROUPS,
} from "helpers/companyPortalConfig";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Row,
} from "reactstrap";
import CustomPageResourcePicker from "./CustomPageResourcePicker";

const sectionCardStyle = { border: "1px solid #e9ecef" };
const IMAGE_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";
const MAX_PORTAL_CUSTOM_HTML_LEN = 1048576;
const SUPPORTED_IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
]);
const SUPPORTED_IMAGE_EXTENSIONS = new Set([".png", ".jpg", ".jpeg", ".webp", ".svg"]);

const getFileExt = (name = "") => (name ? name.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] || "" : "");

const validateImageFile = (file) => {
  if (!file) return "";

  if (SUPPORTED_IMAGE_MIME_TYPES.has(file.type) || SUPPORTED_IMAGE_EXTENSIONS.has(getFileExt(file.name))) {
    return "";
  }

  return "Only PNG, JPG, JPEG, WEBP, and SVG images are supported.";
};

const blankCard = () => ({
  label: "",
  href: "",
  imageUrl: "",
  imageKey: "",
  imageAlt: "",
});

const blankContact = () => ({
  email: "",
  name: "",
  title: "",
  organization: "",
});

const blankLink = () => ({
  label: "",
  href: "",
});

const blankSidebarItem = (sortOrder = 0) => ({
  id: `item-${Date.now()}-${sortOrder}`,
  label: "",
  type: "route",
  path: "/home",
  scrollTarget: "contact-info",
  href: "",
  openInNewTab: true,
  enabled: true,
  sortOrder,
});

const blankCustomPage = () => ({
  slug: "",
  html: "",
  documentIds: [],
});

const ToggleInput = ({ id, label, checked, onChange, className = "" }) => (
  <div
    id={id}
    className={`d-flex align-items-center justify-content-between gap-3 ${className}`.trim()}
  >
    <Label className="mb-0">{label}</Label>
    <Button
      type="button"
      color={checked ? "success" : "secondary"}
      outline={!checked}
      onClick={() => onChange(!checked)}
      style={{ minWidth: 110 }}
    >
      {checked ? "Enabled" : "Disabled"}
    </Button>
  </div>
);

const PortalCustomizationForm = ({
  dashboardConfig,
  setDashboardConfig,
  sidebarConfig,
  setSidebarConfig,
  onUploadMedia,
  portalCustomHtmlEnabled,
  setPortalCustomHtmlEnabled,
  portalCustomHtml,
  setPortalCustomHtml,
  canEditPortalHtmlOverride = true,
  customPages,
  setCustomPages,
  companyDocuments = [],
  companyDocumentsReady = false,
  companyDocumentsError = "",
}) => {
  const [uploadError, setUploadError] = useState("");
  const [uploadingKey, setUploadingKey] = useState("");

  const sidebarItems = useMemo(
    () => (Array.isArray(sidebarConfig?.items) ? sidebarConfig.items : []),
    [sidebarConfig]
  );

  const customPageSidebarWarnings = useMemo(
    () => getCustomPageSidebarWarnings(customPages, sidebarItems),
    [customPages, sidebarItems]
  );

  const updateCustomPage = (index, key, value) => {
    setCustomPages((prev) => {
      const next = [...(prev || [])];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const addCustomPage = () => {
    setCustomPages((prev) => [...(prev || []), blankCustomPage()]);
  };

  const removeCustomPage = (index) => {
    setCustomPages((prev) => (prev || []).filter((_, i) => i !== index));
  };

  const customPagesEditorDisabled = !canEditPortalHtmlOverride;

  const renderCustomPortalPagesSection = () => {
    const hasSidebarDriftWarnings =
      customPageSidebarWarnings.missingPageRoutes.length > 0 ||
      customPageSidebarWarnings.unlinkedSlugs.length > 0;
    const sidebarRoutesLockedByOverride = portalCustomHtmlEnabled && canEditPortalHtmlOverride;

    return (
    <Card className="mb-4" style={sectionCardStyle}>
      <CardHeader className="bg-light">Custom Portal Pages</CardHeader>
      <CardBody>
        <p className="text-muted">
          Optional HTML pages at <code>/home/&lt;slug&gt;</code>. Sidebar routes and these pages are not kept in sync
          automatically — check the warnings below after you change either side.
        </p>

        {sidebarRoutesLockedByOverride && hasSidebarDriftWarnings ? (
          <Alert color="info" className="mb-3" fade={false} data-testid="custom-pages-override-sidebar-note">
            Full HTML override is on. Use the <strong>Role 3 Sidebar Items</strong> section on this page to add or
            change routes that match these pages.
          </Alert>
        ) : null}

        {hasSidebarDriftWarnings && (
          <Alert color="warning" className="mb-3" fade={false}>
            {customPageSidebarWarnings.missingPageRoutes.length > 0 ? (
              <div className="mb-2" data-testid="custom-pages-missing-sidebar-target-warning">
                <strong>Sidebar routes point to missing custom pages</strong>
                <p className="mb-1 small">
                  Add a page with the same slug, or update the sidebar path. Affected routes:
                </p>
                <ul className="mb-0 small">
                  {customPageSidebarWarnings.missingPageRoutes.map((route) => (
                    <li key={route}>
                      <code>{route}</code>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {customPageSidebarWarnings.unlinkedSlugs.length > 0 ? (
              <div data-testid="custom-pages-unlinked-page-warning">
                <strong>Custom pages have no matching sidebar route</strong>
                <p className="mb-1 small">
                  Add a role-3 sidebar item of type &quot;Route&quot; with path <code>/home/&lt;slug&gt;</code>, or
                  remove the page. Slugs without a link:
                </p>
                <ul className="mb-0 small">
                  {customPageSidebarWarnings.unlinkedSlugs.map((slug) => (
                    <li key={slug}>
                      <code>{slug}</code>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Alert>
        )}

        {customPagesEditorDisabled ? (
          <p className="text-muted small">Only a superadmin can add or edit custom portal pages.</p>
        ) : (
          <div className="d-flex justify-content-end mb-3">
            <Button color="light" type="button" onClick={addCustomPage}>
              Add page
            </Button>
          </div>
        )}

        {(customPages || []).map((page, index) => {
          const previewRoute = getCustomPageRoute(page?.slug);
          return (
            <Card className="mb-3" key={`custom-page-${index}`} data-testid="custom-page-row">
              <CardBody>
                <Row>
                  <Col md={4} className="mb-2">
                    <Label>Slug (kebab-case)</Label>
                    <Input
                      value={page?.slug ?? ""}
                      disabled={customPagesEditorDisabled}
                      onChange={(e) => updateCustomPage(index, "slug", e.target.value)}
                      placeholder="e.g. safety-overview"
                    />
                  </Col>
                  <Col md={8} className="mb-2">
                    <Label>Preview route</Label>
                    <Input
                      readOnly
                      plaintext
                      value={previewRoute || "(enter a valid slug)"}
                    />
                  </Col>
                  <Col md={12} className="mb-2">
                    <Label>HTML</Label>
                    <Input
                      type="textarea"
                      rows={6}
                      className="font-monospace small"
                      value={page?.html ?? ""}
                      disabled={customPagesEditorDisabled}
                      onChange={(e) => updateCustomPage(index, "html", e.target.value)}
                      spellCheck={false}
                    />
                  </Col>
                  <Col md={12} className="mb-2">
                    <CustomPageResourcePicker
                      documentIds={page?.documentIds || []}
                      onChange={(documentIds) => updateCustomPage(index, "documentIds", documentIds)}
                      documents={companyDocuments}
                      documentsReady={companyDocumentsReady}
                      documentsError={companyDocumentsError}
                      disabled={customPagesEditorDisabled}
                    />
                  </Col>
                  <Col md={12}>
                    <Button
                      color="danger"
                      outline
                      block
                      type="button"
                      disabled={customPagesEditorDisabled}
                      onClick={() => removeCustomPage(index)}
                    >
                      Remove page
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          );
        })}
      </CardBody>
    </Card>
    );
  };

  const renderRole3SidebarItemsSection = () => (
    <Card className="mb-4" style={sectionCardStyle}>
      <CardHeader className="bg-light">Role 3 Sidebar Items</CardHeader>
      <CardBody>
        <p className="text-muted">
          Sidebar items can be internal routes, in-page scroll anchors, or external links. Icons use{" "}
          <a href="https://boxicons.com/" target="_blank" rel="noopener noreferrer">
            Boxicons
          </a>{" "}
          (outline <code>bx</code> or solid <code>bxs</code>).
        </p>
        <div className="d-flex justify-content-end mb-3">
          <Button color="light" onClick={addSidebarItem}>
            Add Sidebar Item
          </Button>
        </div>
        {sidebarItems.map((item, index) => (
          <Card className="mb-3" key={item.id || `sidebar-item-${index}`}>
            <CardBody>
              <Row>
                <Col md={3} className="mb-3">
                  <Label>Label</Label>
                  <Input
                    value={item.label}
                    onChange={(e) => updateSidebarItem(index, "label", e.target.value)}
                  />
                </Col>
                <Col md={2} className="mb-3">
                  <Label>Type</Label>
                  <Input
                    type="select"
                    value={item.type}
                    onChange={(e) => updateSidebarItem(index, "type", e.target.value)}
                  >
                    <option value="route">Route</option>
                    <option value="scroll">Scroll</option>
                    <option value="external">External</option>
                  </Input>
                </Col>
                <Col md={2} className="mb-3">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={item.sortOrder}
                    onChange={(e) => updateSidebarItem(index, "sortOrder", Number(e.target.value))}
                  />
                </Col>
                <Col md={2} className="mb-3 d-flex align-items-center">
                  <ToggleInput
                    id={`sidebar-item-enabled-${index}`}
                    label="Enabled"
                    checked={item.enabled !== false}
                    onChange={(checked) => updateSidebarItem(index, "enabled", checked)}
                    className="mt-4"
                  />
                </Col>
                <Col md={3} className="mb-3 d-flex align-items-end">
                  <Button color="danger" outline block onClick={() => removeSidebarItem(index)}>
                    Remove
                  </Button>
                </Col>

                <Col md={8} className="mb-3">
                  <Label className="d-flex align-items-center gap-2">
                    Icon (Boxicons)
                    <i className={getSidebarItemIconClass(item)} title="Preview" aria-hidden />
                  </Label>
                  <Input
                    type="select"
                    value={
                      item.iconClass &&
                      !ALL_SIDEBAR_ICON_PRESET_OPTIONS.some((o) => o.value === item.iconClass)
                        ? item.iconClass
                        : item.iconClass || ""
                    }
                    onChange={(e) => updateSidebarItem(index, "iconClass", e.target.value)}
                  >
                    {(() => {
                      const v = item.iconClass;
                      const extraNotInList =
                        v && !ALL_SIDEBAR_ICON_PRESET_OPTIONS.some((o) => o.value === v)
                          ? [{ value: v, label: `Saved class: ${v}` }]
                          : [];
                      return SIDEBAR_ICON_PRESET_GROUPS.map((section) => {
                        const options =
                          section.group === "Default" && extraNotInList.length
                            ? [...section.options, ...extraNotInList]
                            : section.options;
                        return (
                          <optgroup key={section.group} label={section.group}>
                            {options.map((opt, optIdx) => (
                              <option key={`${section.group}-${opt.value}-${optIdx}`} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        );
                      });
                    })()}
                  </Input>
                </Col>

                {item.type === "route" ? (
                  <Col md={12} className="mb-2">
                    <Label>Internal Path</Label>
                    <Input
                      value={item.path || ""}
                      onChange={(e) => updateSidebarItem(index, "path", e.target.value)}
                    />
                  </Col>
                ) : null}

                {item.type === "scroll" ? (
                  <Col md={12} className="mb-2">
                    <Label>Scroll Target ID</Label>
                    <Input
                      value={item.scrollTarget || ""}
                      onChange={(e) => updateSidebarItem(index, "scrollTarget", e.target.value)}
                    />
                  </Col>
                ) : null}

                {item.type === "external" ? (
                  <>
                    <Col md={8} className="mb-2">
                      <Label>External URL</Label>
                      <Input
                        value={item.href || ""}
                        onChange={(e) => updateSidebarItem(index, "href", e.target.value)}
                      />
                    </Col>
                    <Col md={4} className="mb-2 d-flex align-items-center">
                      <ToggleInput
                        id={`sidebar-item-new-tab-${index}`}
                        label="Open in new tab"
                        checked={item.openInNewTab !== false}
                        onChange={(checked) => updateSidebarItem(index, "openInNewTab", checked)}
                        className="mt-4"
                      />
                    </Col>
                  </>
                ) : null}
              </Row>
            </CardBody>
          </Card>
        ))}
      </CardBody>
    </Card>
  );

  const updateSection = (sectionName, key, value) => {
    setDashboardConfig((prev) => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        [key]: value,
      },
    }));
  };

  const updateArrayItem = (sectionName, arrayName, index, key, value) => {
    setDashboardConfig((prev) => {
      const nextItems = [...(prev?.[sectionName]?.[arrayName] || [])];
      nextItems[index] = {
        ...nextItems[index],
        [key]: value,
      };
      return {
        ...prev,
        [sectionName]: {
          ...prev[sectionName],
          [arrayName]: nextItems,
        },
      };
    });
  };

  const updateArrayValue = (sectionName, arrayName, index, value) => {
    setDashboardConfig((prev) => {
      const nextItems = [...(prev?.[sectionName]?.[arrayName] || [])];
      nextItems[index] = value;
      return {
        ...prev,
        [sectionName]: {
          ...prev[sectionName],
          [arrayName]: nextItems,
        },
      };
    });
  };

  const addArrayItem = (sectionName, arrayName, factory) => {
    setDashboardConfig((prev) => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        [arrayName]: [...(prev?.[sectionName]?.[arrayName] || []), factory()],
      },
    }));
  };

  const removeArrayItem = (sectionName, arrayName, index) => {
    setDashboardConfig((prev) => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        [arrayName]: (prev?.[sectionName]?.[arrayName] || []).filter((_, itemIndex) => itemIndex !== index),
      },
    }));
  };

  const updateSidebarItem = (index, key, value) => {
    setSidebarConfig((prev) => {
      const items = [...(prev?.items || [])];
      const next = { ...items[index] };
      if (key === "iconClass" && (value === "" || value == null)) {
        delete next.iconClass;
      } else {
        next[key] = value;
      }
      items[index] = next;
      return { items };
    });
  };

  const addSidebarItem = () => {
    setSidebarConfig((prev) => ({
      items: [...(prev?.items || []), blankSidebarItem((prev?.items || []).length)],
    }));
  };

  const removeSidebarItem = (index) => {
    setSidebarConfig((prev) => ({
      items: (prev?.items || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleCardImageUpload = async (index, file) => {
    if (!file) return;

    const key = `lab-card-${index}`;
    try {
      const fileError = validateImageFile(file);
      if (fileError) {
        setUploadError(fileError);
        return;
      }

      setUploadError("");
      setUploadingKey(key);
      const uploaded = await onUploadMedia(file, "public");
      updateArrayItem("lab", "cards", index, "imageUrl", uploaded.publicUrl || uploaded.logoUrl);
      updateArrayItem("lab", "cards", index, "imageKey", uploaded.key || uploaded.logoKey || "");
      if (!dashboardConfig?.lab?.cards?.[index]?.imageAlt) {
        updateArrayItem("lab", "cards", index, "imageAlt", dashboardConfig?.lab?.cards?.[index]?.label || "Portal card image");
      }
    } catch (error) {
      setUploadError(error?.message || "Image upload failed.");
    } finally {
      setUploadingKey("");
    }
  };

  const handleQuoteHighlightsImageUpload = async (file) => {
    if (!file) return;

    const key = "quote-highlights-image";
    try {
      const fileError = validateImageFile(file);
      if (fileError) {
        setUploadError(fileError);
        return;
      }

      setUploadError("");
      setUploadingKey(key);
      const uploaded = await onUploadMedia(file, "public");
      updateSection("quoteHighlights", "imageUrl", uploaded.publicUrl || uploaded.logoUrl);
      updateSection("quoteHighlights", "imageKey", uploaded.key || uploaded.logoKey || "");
      if (!dashboardConfig?.quoteHighlights?.imageAlt) {
        updateSection("quoteHighlights", "imageAlt", "Portal feature image");
      }
    } catch (error) {
      setUploadError(error?.message || "Image upload failed.");
    } finally {
      setUploadingKey("");
    }
  };

  if (portalCustomHtmlEnabled && canEditPortalHtmlOverride) {
    return (
      <div className="mt-4">
        <h5 className="mb-3">Portal Customization — full HTML override</h5>
        <Alert color="warning" className="mb-3" fade={false}>
          Trusted superadmin content: HTML runs in the portal user&apos;s browser with full access to the session
          origin. Only use if you understand XSS and script risks.
        </Alert>
        {uploadError ? <Alert color="danger">{uploadError}</Alert> : null}
        <Card className="mb-4" style={sectionCardStyle}>
          <CardBody>
            <ToggleInput
              id="portal-html-override-toggle"
              label="Full HTML override"
              checked={portalCustomHtmlEnabled}
              onChange={(checked) => setPortalCustomHtmlEnabled(checked)}
              className="mb-3"
            />
            <Label for="portal-custom-html">Custom HTML (inside portal page only)</Label>
            <Input
              id="portal-custom-html"
              type="textarea"
              rows={22}
              className="font-monospace small"
              value={portalCustomHtml}
              onChange={(e) => setPortalCustomHtml(e.target.value)}
              spellCheck={false}
              maxLength={MAX_PORTAL_CUSTOM_HTML_LEN}
            />
            <p className="text-muted small mt-2 mb-0">
              Structured portal fields (hero, sections, etc.) are hidden while override is on. They remain saved on the
              server and return when you disable override. Role 3 sidebar items stay editable below. Verify on /home as
              a role-3 user after saving.
            </p>
          </CardBody>
        </Card>
        {renderCustomPortalPagesSection()}
        {renderRole3SidebarItemsSection()}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <h5 className="mb-3">Portal Customization</h5>
      <p className="text-muted mb-4">
        The left header logo stays fixed to ColdBlock. The right header logo will use the company logo above.
      </p>

      {canEditPortalHtmlOverride ? (
        <Card className="mb-4" style={sectionCardStyle}>
          <CardBody>
            <ToggleInput
              id="portal-html-override-off-toggle"
              label="Full HTML override (hides structured fields when enabled)"
              checked={!!portalCustomHtmlEnabled}
              onChange={(checked) => setPortalCustomHtmlEnabled(checked)}
              className="mb-3"
            />
          </CardBody>
        </Card>
      ) : portalCustomHtmlEnabled ? (
        <Alert color="info" className="mb-3">
          This company uses full HTML portal override. Only a superadmin can edit override settings; structured fields
          below may not match what role-3 users see on /home.
        </Alert>
      ) : null}

      {uploadError ? <Alert color="danger">{uploadError}</Alert> : null}

      <Card className="mb-4" style={sectionCardStyle}>
        <CardHeader className="bg-light">Hero Section</CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <ToggleInput
                id="portal-toggle-hero"
                label="Show Hero"
                checked={dashboardConfig.hero.enabled}
                onChange={(checked) => updateSection("hero", "enabled", checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Label>Portal Title</Label>
              <Input
                value={dashboardConfig.hero.title}
                onChange={(e) => updateSection("hero", "title", e.target.value)}
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label>Video URL</Label>
              <Input
                value={dashboardConfig.hero.videoUrl}
                onChange={(e) => updateSection("hero", "videoUrl", e.target.value)}
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label>Intro HTML</Label>
              <Input
                type="textarea"
                rows="6"
                value={dashboardConfig.hero.introHtml}
                onChange={(e) => updateSection("hero", "introHtml", e.target.value)}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4" style={sectionCardStyle}>
        <CardHeader className="bg-light">Technology Section</CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <ToggleInput
                id="portal-toggle-technology"
                label="Show Technology Section"
                checked={dashboardConfig.technology.enabled}
                onChange={(checked) => updateSection("technology", "enabled", checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label>Section Heading</Label>
              <Input
                value={dashboardConfig.technology.heading}
                onChange={(e) => updateSection("technology", "heading", e.target.value)}
              />
            </Col>
            {dashboardConfig.technology.paragraphs.map((paragraph, index) => (
              <Col md={12} className="mb-3" key={`technology-paragraph-${index}`}>
                <Label>{`Paragraph ${index + 1}`}</Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={paragraph}
                  onChange={(e) => {
                    const paragraphs = [...dashboardConfig.technology.paragraphs];
                    paragraphs[index] = e.target.value;
                    updateSection("technology", "paragraphs", paragraphs);
                  }}
                />
              </Col>
            ))}
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4" style={sectionCardStyle}>
        <CardHeader className="bg-light">Highlight Band Section</CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <ToggleInput
                id="portal-toggle-highlight-band"
                label="Show Highlight Band"
                checked={dashboardConfig.highlightBand.enabled}
                onChange={(checked) => updateSection("highlightBand", "enabled", checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Label>Background Color</Label>
              <div className="d-flex gap-2">
                <Input
                  type="color"
                  value={dashboardConfig.highlightBand.backgroundColor || "#f1f1f1"}
                  style={{ maxWidth: 50, minHeight: 40 }}
                  onChange={(e) => updateSection("highlightBand", "backgroundColor", e.target.value)}
                />
                <Input
                  value={dashboardConfig.highlightBand.backgroundColor || ""}
                  placeholder="#f1f1f1"
                  onChange={(e) => updateSection("highlightBand", "backgroundColor", e.target.value)}
                />
              </div>
            </Col>
            <Col md={12} className="mb-3">
              <Label>Card Header HTML</Label>
              <Input
                type="textarea"
                rows="3"
                value={dashboardConfig.highlightBand.headerHtml}
                onChange={(e) => updateSection("highlightBand", "headerHtml", e.target.value)}
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label>Body HTML</Label>
              <Input
                type="textarea"
                rows="4"
                value={dashboardConfig.highlightBand.bodyHtml}
                onChange={(e) => updateSection("highlightBand", "bodyHtml", e.target.value)}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4" style={sectionCardStyle}>
        <CardHeader className="bg-light">Quote and Highlights Section</CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <ToggleInput
                id="portal-toggle-quote-highlights"
                label="Show Quote and Highlights"
                checked={dashboardConfig.quoteHighlights.enabled}
                onChange={(checked) => updateSection("quoteHighlights", "enabled", checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label>Quote HTML</Label>
              <Input
                type="textarea"
                rows="6"
                value={dashboardConfig.quoteHighlights.quoteHtml}
                onChange={(e) => updateSection("quoteHighlights", "quoteHtml", e.target.value)}
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label>Body HTML</Label>
              <Input
                type="textarea"
                rows="4"
                value={dashboardConfig.quoteHighlights.bodyHtml}
                onChange={(e) => updateSection("quoteHighlights", "bodyHtml", e.target.value)}
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label>Feature Image</Label>
              <Input
                type="file"
                accept={IMAGE_ACCEPT}
                onChange={(e) => handleQuoteHighlightsImageUpload(e.target.files?.[0])}
              />
              {uploadingKey === "quote-highlights-image" ? (
                <div className="small text-muted mt-2">Uploading image...</div>
              ) : null}
            </Col>
            <Col md={6} className="mb-3">
              <Label>Image Alt Text</Label>
              <Input
                value={dashboardConfig.quoteHighlights.imageAlt || ""}
                onChange={(e) => updateSection("quoteHighlights", "imageAlt", e.target.value)}
              />
            </Col>
            {dashboardConfig.quoteHighlights.imageUrl ? (
              <Col md={12} className="mb-3">
                <img
                  src={dashboardConfig.quoteHighlights.imageUrl}
                  alt={dashboardConfig.quoteHighlights.imageAlt || "Portal feature"}
                  style={{ maxHeight: 180, maxWidth: "100%" }}
                />
              </Col>
            ) : null}
          </Row>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Highlights List</h6>
            <Button color="light" onClick={() => addArrayItem("quoteHighlights", "highlights", () => "")}>
              Add Highlight
            </Button>
          </div>
          {dashboardConfig.quoteHighlights.highlights.map((item, index) => (
            <Card className="mb-3" key={`quote-highlight-${index}`}>
              <CardBody>
                <Row>
                  <Col md={10} className="mb-2">
                    <Label>{`Highlight ${index + 1} HTML`}</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={item}
                      onChange={(e) => updateArrayValue("quoteHighlights", "highlights", index, e.target.value)}
                    />
                  </Col>
                  <Col md={2} className="mb-2 d-flex align-items-end">
                    <Button color="danger" outline block onClick={() => removeArrayItem("quoteHighlights", "highlights", index)}>
                      Remove
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          ))}
        </CardBody>
      </Card>

      <Card className="mb-4" style={sectionCardStyle}>
        <CardHeader className="bg-light">ColdBlock R&amp;D Lab</CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <ToggleInput
                id="portal-toggle-lab"
                label="Show Lab Section"
                checked={dashboardConfig.lab.enabled}
                onChange={(checked) => updateSection("lab", "enabled", checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Label>Section Heading</Label>
              <Input
                value={dashboardConfig.lab.heading}
                onChange={(e) => updateSection("lab", "heading", e.target.value)}
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label>Intro Paragraph</Label>
              <Input
                type="textarea"
                rows="4"
                value={dashboardConfig.lab.intro}
                onChange={(e) => updateSection("lab", "intro", e.target.value)}
              />
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Feature Cards</h6>
            <Button color="light" onClick={() => addArrayItem("lab", "cards", blankCard)}>
              Add Card
            </Button>
          </div>
          {dashboardConfig.lab.cards.map((card, index) => (
            <Card className="mb-3" key={`lab-card-${index}`}>
              <CardBody>
                <Row>
                  <Col md={5} className="mb-3">
                    <Label>Label</Label>
                    <Input
                      value={card.label}
                      onChange={(e) => updateArrayItem("lab", "cards", index, "label", e.target.value)}
                    />
                  </Col>
                  <Col md={5} className="mb-3">
                    <Label>Destination URL</Label>
                    <Input
                      value={card.href}
                      onChange={(e) => updateArrayItem("lab", "cards", index, "href", e.target.value)}
                    />
                  </Col>
                  <Col md={2} className="mb-3 d-flex align-items-end">
                    <Button color="danger" outline block onClick={() => removeArrayItem("lab", "cards", index)}>
                      Remove
                    </Button>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Card Image</Label>
                    <Input
                      type="file"
                      accept={IMAGE_ACCEPT}
                      onChange={(e) => handleCardImageUpload(index, e.target.files?.[0])}
                    />
                    {uploadingKey === `lab-card-${index}` ? (
                      <div className="small text-muted mt-2">Uploading image...</div>
                    ) : null}
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Image Alt Text</Label>
                    <Input
                      value={card.imageAlt || ""}
                      onChange={(e) => updateArrayItem("lab", "cards", index, "imageAlt", e.target.value)}
                    />
                  </Col>
                  {shouldShowLabCardImagePreview(card) ? (
                    <Col md={12}>
                      <img src={card.imageUrl} alt={card.imageAlt || card.label || "Portal card"} style={{ maxHeight: 72 }} />
                    </Col>
                  ) : null}
                </Row>
              </CardBody>
            </Card>
          ))}

          <Row className="mt-4">
            <Col md={12} className="mb-3">
              <Label>Lab Contact Intro</Label>
              <Input
                type="textarea"
                rows="3"
                value={dashboardConfig.lab.contactIntro}
                onChange={(e) => updateSection("lab", "contactIntro", e.target.value)}
              />
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Lab Contacts</h6>
            <Button color="light" onClick={() => addArrayItem("lab", "contacts", blankContact)}>
              Add Contact
            </Button>
          </div>
          {dashboardConfig.lab.contacts.map((contact, index) => (
            <Card className="mb-3" key={`lab-contact-${index}`}>
              <CardBody>
                <Row>
                  <Col md={3} className="mb-3">
                    <Label>Email</Label>
                    <Input
                      value={contact.email}
                      onChange={(e) => updateArrayItem("lab", "contacts", index, "email", e.target.value)}
                    />
                  </Col>
                  <Col md={3} className="mb-3">
                    <Label>Name</Label>
                    <Input
                      value={contact.name || ""}
                      onChange={(e) => updateArrayItem("lab", "contacts", index, "name", e.target.value)}
                    />
                  </Col>
                  <Col md={4} className="mb-3">
                    <Label>Title</Label>
                    <Input
                      value={contact.title || ""}
                      onChange={(e) => updateArrayItem("lab", "contacts", index, "title", e.target.value)}
                    />
                  </Col>
                  <Col md={2} className="mb-3 d-flex align-items-end">
                    <Button color="danger" outline block onClick={() => removeArrayItem("lab", "contacts", index)}>
                      Remove
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          ))}
        </CardBody>
      </Card>

      <Card className="mb-4" style={sectionCardStyle}>
        <CardHeader className="bg-light">Important Contacts</CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <ToggleInput
                id="portal-toggle-important-contacts"
                label="Show Important Contacts"
                checked={dashboardConfig.importantContacts.enabled}
                onChange={(checked) => updateSection("importantContacts", "enabled", checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label>Main Heading</Label>
              <Input
                value={dashboardConfig.importantContacts.heading}
                onChange={(e) => updateSection("importantContacts", "heading", e.target.value)}
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label>Left Column Title</Label>
              <Input
                value={dashboardConfig.importantContacts.leftTitle}
                onChange={(e) => updateSection("importantContacts", "leftTitle", e.target.value)}
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label>Right Column Title</Label>
              <Input
                value={dashboardConfig.importantContacts.rightTitle}
                onChange={(e) => updateSection("importantContacts", "rightTitle", e.target.value)}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Left Contacts</h6>
                <Button color="light" onClick={() => addArrayItem("importantContacts", "leftContacts", blankContact)}>
                  Add Contact
                </Button>
              </div>
              {dashboardConfig.importantContacts.leftContacts.map((contact, index) => (
                <Card className="mb-3" key={`left-contact-${index}`}>
                  <CardBody>
                    <Row>
                      <Col md={12} className="mb-2">
                        <Label>Email</Label>
                        <Input
                          value={contact.email}
                          onChange={(e) => updateArrayItem("importantContacts", "leftContacts", index, "email", e.target.value)}
                        />
                      </Col>
                      <Col md={12} className="mb-2">
                        <Label>Name</Label>
                        <Input
                          value={contact.name || ""}
                          onChange={(e) => updateArrayItem("importantContacts", "leftContacts", index, "name", e.target.value)}
                        />
                      </Col>
                      <Col md={12} className="mb-2">
                        <Label>Title</Label>
                        <Input
                          value={contact.title || ""}
                          onChange={(e) => updateArrayItem("importantContacts", "leftContacts", index, "title", e.target.value)}
                        />
                      </Col>
                      <Col md={12} className="mb-2">
                        <Label>Organization</Label>
                        <Input
                          value={contact.organization || ""}
                          onChange={(e) => updateArrayItem("importantContacts", "leftContacts", index, "organization", e.target.value)}
                        />
                      </Col>
                      <Col md={12}>
                        <Button color="danger" outline block onClick={() => removeArrayItem("importantContacts", "leftContacts", index)}>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Right Contacts</h6>
                <Button color="light" onClick={() => addArrayItem("importantContacts", "rightContacts", blankContact)}>
                  Add Contact
                </Button>
              </div>
              {dashboardConfig.importantContacts.rightContacts.map((contact, index) => (
                <Card className="mb-3" key={`right-contact-${index}`}>
                  <CardBody>
                    <Row>
                      <Col md={12} className="mb-2">
                        <Label>Email</Label>
                        <Input
                          value={contact.email}
                          onChange={(e) => updateArrayItem("importantContacts", "rightContacts", index, "email", e.target.value)}
                        />
                      </Col>
                      <Col md={12} className="mb-2">
                        <Label>Name</Label>
                        <Input
                          value={contact.name || ""}
                          onChange={(e) => updateArrayItem("importantContacts", "rightContacts", index, "name", e.target.value)}
                        />
                      </Col>
                      <Col md={12} className="mb-2">
                        <Label>Title</Label>
                        <Input
                          value={contact.title || ""}
                          onChange={(e) => updateArrayItem("importantContacts", "rightContacts", index, "title", e.target.value)}
                        />
                      </Col>
                      <Col md={12} className="mb-2">
                        <Label>Organization</Label>
                        <Input
                          value={contact.organization || ""}
                          onChange={(e) => updateArrayItem("importantContacts", "rightContacts", index, "organization", e.target.value)}
                        />
                      </Col>
                      <Col md={12}>
                        <Button color="danger" outline block onClick={() => removeArrayItem("importantContacts", "rightContacts", index)}>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4" style={sectionCardStyle}>
        <CardHeader className="bg-light">Support Section</CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <ToggleInput
                id="portal-toggle-support"
                label="Show Support Section"
                checked={dashboardConfig.support.enabled}
                onChange={(checked) => updateSection("support", "enabled", checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Label>Resources Heading</Label>
              <Input
                value={dashboardConfig.support.resourcesHeading}
                onChange={(e) => updateSection("support", "resourcesHeading", e.target.value)}
              />
            </Col>
            <Col md={6} className="mb-3">
              <Label>Support Heading</Label>
              <Input
                value={dashboardConfig.support.supportHeading}
                onChange={(e) => updateSection("support", "supportHeading", e.target.value)}
              />
            </Col>
            <Col md={12} className="mb-3">
              <Label>Resources Body HTML</Label>
              <Input
                type="textarea"
                rows="4"
                value={dashboardConfig.support.resourcesBodyHtml}
                onChange={(e) => updateSection("support", "resourcesBodyHtml", e.target.value)}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Support Items</h6>
            <Button color="light" onClick={() => addArrayItem("support", "supportItems", () => "")}>
              Add Support Item
            </Button>
          </div>
          {dashboardConfig.support.supportItems.map((item, index) => (
            <Card className="mb-3" key={`support-item-${index}`}>
              <CardBody>
                <Row>
                  <Col md={10} className="mb-2">
                    <Label>{`Support Item ${index + 1} HTML`}</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={item}
                      onChange={(e) => {
                        const items = [...dashboardConfig.support.supportItems];
                        items[index] = e.target.value;
                        updateSection("support", "supportItems", items);
                      }}
                    />
                  </Col>
                  <Col md={2} className="mb-2 d-flex align-items-end">
                    <Button color="danger" outline block onClick={() => removeArrayItem("support", "supportItems", index)}>
                      Remove
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          ))}
        </CardBody>
      </Card>

      <Card className="mb-4" style={sectionCardStyle}>
        <CardHeader className="bg-light">External Links Section</CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <ToggleInput
                id="portal-toggle-link-columns"
                label="Show External Links Section"
                checked={dashboardConfig.linkColumns.enabled}
                onChange={(checked) => updateSection("linkColumns", "enabled", checked)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label>Heading</Label>
              <Input
                value={dashboardConfig.linkColumns.heading}
                onChange={(e) => updateSection("linkColumns", "heading", e.target.value)}
              />
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Left Column Links</h6>
                <Button color="light" onClick={() => addArrayItem("linkColumns", "leftLinks", blankLink)}>
                  Add Link
                </Button>
              </div>
              {dashboardConfig.linkColumns.leftLinks.map((link, index) => (
                <Card className="mb-3" key={`left-link-${index}`}>
                  <CardBody>
                    <Row>
                      <Col md={12} className="mb-2">
                        <Label>Link Label</Label>
                        <Input
                          value={link.label}
                          onChange={(e) => updateArrayItem("linkColumns", "leftLinks", index, "label", e.target.value)}
                        />
                      </Col>
                      <Col md={12} className="mb-2">
                        <Label>External URL</Label>
                        <Input
                          value={link.href}
                          onChange={(e) => updateArrayItem("linkColumns", "leftLinks", index, "href", e.target.value)}
                        />
                      </Col>
                      <Col md={12}>
                        <Button color="danger" outline block onClick={() => removeArrayItem("linkColumns", "leftLinks", index)}>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}
            </Col>

            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Right Column Links</h6>
                <Button color="light" onClick={() => addArrayItem("linkColumns", "rightLinks", blankLink)}>
                  Add Link
                </Button>
              </div>
              {dashboardConfig.linkColumns.rightLinks.map((link, index) => (
                <Card className="mb-3" key={`right-link-${index}`}>
                  <CardBody>
                    <Row>
                      <Col md={12} className="mb-2">
                        <Label>Link Label</Label>
                        <Input
                          value={link.label}
                          onChange={(e) => updateArrayItem("linkColumns", "rightLinks", index, "label", e.target.value)}
                        />
                      </Col>
                      <Col md={12} className="mb-2">
                        <Label>External URL</Label>
                        <Input
                          value={link.href}
                          onChange={(e) => updateArrayItem("linkColumns", "rightLinks", index, "href", e.target.value)}
                        />
                      </Col>
                      <Col md={12}>
                        <Button color="danger" outline block onClick={() => removeArrayItem("linkColumns", "rightLinks", index)}>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {renderCustomPortalPagesSection()}

      {renderRole3SidebarItemsSection()}
    </div>
  );
};

PortalCustomizationForm.propTypes = {
  dashboardConfig: PropTypes.object.isRequired,
  setDashboardConfig: PropTypes.func.isRequired,
  sidebarConfig: PropTypes.object.isRequired,
  setSidebarConfig: PropTypes.func.isRequired,
  onUploadMedia: PropTypes.func.isRequired,
  portalCustomHtmlEnabled: PropTypes.bool.isRequired,
  setPortalCustomHtmlEnabled: PropTypes.func.isRequired,
  portalCustomHtml: PropTypes.string.isRequired,
  setPortalCustomHtml: PropTypes.func.isRequired,
  canEditPortalHtmlOverride: PropTypes.bool,
  customPages: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      html: PropTypes.string,
      documentIds: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  setCustomPages: PropTypes.func.isRequired,
  companyDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      fileName: PropTypes.string,
    })
  ),
  companyDocumentsReady: PropTypes.bool,
  companyDocumentsError: PropTypes.string,
};

export default PortalCustomizationForm;
